import dayjs, { Dayjs } from 'dayjs';
import { isEmpty } from 'lodash-es';
import { IndexTimeActive as UnityPowerTimeActive } from './components/UnityPowerMap/components/LeftBox/components/SwitchDate';
import { DateTypeByWater } from './components/UnityWaterMap/components/LeftBox';
import { IndexTimeActive as UnityEnergyTimeActive } from './components/UnityEnergyCockpitMap/components/LeftBox/components/SwitchDate';
import { IndexTimeActive as UnityGasTimeActive } from './components/UnityGasMonitorMap/components/LeftBox/SwitchDate';

export const setFitScale = (
  targetContainer?: HTMLDivElement,
  currentClientWidth?: number,
  currentClientHeight?: number,
  isFullScreen?: boolean,
  isStandardSize?: boolean
) => {
  if (!targetContainer || !currentClientWidth || !currentClientHeight) return;
  // 3147 4576
  // const designWidth = isStandardSize ? 1920 : 3147;
  const designWidth = isStandardSize ? 1920 : 2970;
  // const designWidth = 1920;

  let scale = currentClientWidth / designWidth;

  // 实际容器的高度(减去页面顶部导航栏的高度)
  let containerHeight = isFullScreen
    ? document.documentElement.clientHeight
    : document.documentElement.clientHeight - 50;

  /**
   * 1. 当前容器的宽度>=设计稿的宽度时，才进行缩放
   * 2. 先等比缩放, 只考虑宽度, 高度未撑满的话, 取容器高度, 然后再进行flex布局
   *
   */

  if (currentClientWidth >= designWidth) {
    // let currentH = containerHeight >= designHeight ? containerHeight : designHeight;
    let currentH = containerHeight;

    targetContainer.style.width = `${designWidth}px`;

    targetContainer.style.height = `${currentH / scale}px`;
    targetContainer.style.transform = `scale3d(${scale}, ${scale}, ${scale})`;
    targetContainer.style.transformOrigin = 'top left';

    document.documentElement.style.setProperty('--page-scale', String(scale));
    return scale;
  }
  targetContainer.style.width = '';
  targetContainer.style.height = '';
  targetContainer.style.transform = '';
  targetContainer.style.transformOrigin = '';
};

export function goFullscreen(element: HTMLDivElement) {
  if (document.fullscreenElement) {
    return Promise.reject(new Error('全屏模式被禁用'));
  }
  let result = null;
  if (element.requestFullscreen) {
    result = element.requestFullscreen();
  }
  return result || Promise.reject(new Error('不支持全屏'));
}
export function cancelFullscreen() {
  document.exitFullscreen?.();
}

export const getOrderColors = (rank: number) => {
  if (rank === 1) return { backgroundColor: '#A9393E', borderColor: '#843037', progressColor: '#FF4D4F' };
  if (rank === 2) return { backgroundColor: '#A94F28', borderColor: '#844329', progressColor: '#FF6D2C' };
  if (rank === 3) return { backgroundColor: '#A98B3A', borderColor: '#847137', progressColor: '#FFCB47' };
  return { backgroundColor: '#119768', borderColor: '#0F7B58', progressColor: '#16DD8E' };
};

export const calcRatio = (a?: number, b?: number): string => {
  if (!b || b === 0) return '--'; // 避免除以0
  if (a == null || isNaN(a)) return '--';

  const ratio = (a / b) * 100;
  return `${ratio.toFixed(1)}%`;
};

export const calcPercent = (a?: number, b?: number): number => {
  if (!b || b === 0) return 0; // 避免除以0
  if (a == null || isNaN(a)) return 0;

  const ratio = (a / b) * 100;
  return Number(ratio.toFixed(1));
};

export const getDefaultTime = (
  mode: UnityPowerTimeActive | UnityEnergyTimeActive | UnityGasTimeActive,
  date?: Dayjs | null
) => {
  switch (mode) {
    case UnityPowerTimeActive.THIS_MONTH:
    case UnityEnergyTimeActive.THIS_MONTH:
    case UnityGasTimeActive.THIS_MONTH:
      return {
        startTime: dayjs(date).startOf('month').valueOf(),
        endTime: dayjs(date).endOf('month').valueOf(),
      };

    default:
      return {
        startTime: dayjs(date).startOf('year').valueOf(),
        endTime: dayjs(date).endOf('year').valueOf(),
      };
  }
};

export const getDefaultTimeByWaterTrend = (mode: DateTypeByWater, date?: Dayjs | null) => {
  switch (mode) {
    case DateTypeByWater.MONTH:
      return {
        startTime: dayjs(date).startOf('month').valueOf(),
        endTime: dayjs(date).endOf('month').valueOf(),
      };

    default:
      return {
        startTime: dayjs(date).startOf('year').valueOf(),
        endTime: dayjs(date).endOf('year').valueOf(),
      };
  }
};

export function isEmptyString(val: string | null | undefined) {
  return val === '' || val === null || val === undefined;
}

export const ENERGY_MEDIUM_COLOR_PALETTE = [
  '#52E7FF',
  '#FF477B',
  '#FFCB47',
  '#6F47FF',
  '#16DD8E',
  '#2D8DFF',
  '#52C41A',
  '#234ED9',
  '#FF9247',
  '#854ECA',
  '#4A90E2',
];

// 工具函数：将数组按每组 n 个分组
export function chunkArray<T>(arr: T[] = [], size: number): T[][] {
  const result: T[][] = [];
  if (isEmpty(arr)) return [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

export const formatName = (name?: string) => {
  return name ? (name === '新水' ? '自来水' : name) : '--';
};

export const getEnergyMediumColorKey = ({
  energyMediumId,
  energyMediumName,
}: {
  energyMediumId?: string | number;
  energyMediumName?: string;
}) => {
  const normalizedName = formatName(energyMediumName);
  return normalizedName && normalizedName !== '--'
    ? normalizedName
    : energyMediumId != null
    ? String(energyMediumId)
    : '';
};

export const getEnergyMediumColorMap = (
  mediums: Array<{
    energyMediumId?: string | number;
    energyMediumName?: string;
  }> = []
) => {
  const colorMap: Record<string, string> = {};
  let colorIndex = 0;

  mediums.forEach(medium => {
    const key = getEnergyMediumColorKey(medium);
    if (!key || colorMap[key]) return;

    colorMap[key] = ENERGY_MEDIUM_COLOR_PALETTE[colorIndex % ENERGY_MEDIUM_COLOR_PALETTE.length];
    colorIndex += 1;
  });

  return colorMap;
};

export const getEnergyMediumColor = ({
  energyMediumId,
  energyMediumName,
  colorMap,
  fallbackIndex = 0,
}: {
  energyMediumId?: string | number;
  energyMediumName?: string;
  colorMap?: Record<string, string>;
  fallbackIndex?: number;
}) => {
  const key = getEnergyMediumColorKey({ energyMediumId, energyMediumName });

  if (key && colorMap?.[key]) {
    return colorMap[key];
  }

  return ENERGY_MEDIUM_COLOR_PALETTE[fallbackIndex % ENERGY_MEDIUM_COLOR_PALETTE.length];
};

interface NormalizeUnitOptions<T> {
  valueKey: keyof T | ((item: T) => number);

  baseUnit: string;

  threshold?: number;

  fixed?: number;

  prefix?: string;

  /** 是否所有值都满足才转换，默认 true */
  requireAll?: boolean;
}

interface NormalizedResult<T> {
  unit: string;
  scale: number;
  data: T[];
}

export function normalizeChartUnit<T>(datas: T[] | undefined, options: NormalizeUnitOptions<T>): NormalizedResult<T> {
  const { valueKey, baseUnit, threshold = 10000, fixed = 1, prefix = '万', requireAll = false } = options;

  if (!datas?.length) {
    return {
      unit: baseUnit,
      scale: 1,
      data: datas || [],
    };
  }

  const getValue = typeof valueKey === 'function' ? valueKey : (item: T) => Number((item as any)[valueKey]);

  const validValues = datas.map(getValue).filter(v => typeof v === 'number' && !Number.isNaN(v) && v !== 0);

  const needConvert = requireAll
    ? validValues.length > 0 && validValues.every(v => Math.abs(v) >= threshold)
    : validValues.length > 0 && Math.max(...validValues.map(v => Math.abs(v))) >= threshold;

  const scale = needConvert ? threshold : 1;
  const unit = needConvert ? `${prefix}${baseUnit}` : baseUnit;

  return {
    unit,
    scale,
    data: datas.map(item => {
      const rawValue = getValue(item);

      if (rawValue == null || Number.isNaN(rawValue)) {
        return item;
      }

      const nextValue = Number((rawValue / scale).toFixed(fixed));

      if (typeof valueKey === 'function') {
        return {
          ...item,
          __normalizedValue: nextValue,
        } as T;
      }

      return {
        ...item,
        [valueKey]: nextValue,
      };
    }),
  };
}

export interface NormalizeStackedOptions {
  /** series 数组字段名，如 detailList */
  seriesKey: string;
  /** 数值字段名，如 charges */
  valueKey: string;
  baseUnit: string;
  threshold?: number;
  fixed?: number;
  prefix?: string;
  requireAll?: boolean;
}

export function normalizeStackedSeriesUnit(datas: any[] = [], options: NormalizeStackedOptions) {
  const { seriesKey, valueKey, baseUnit, threshold = 10000, fixed = 1, prefix = '万', requireAll = false } = options;

  if (!datas?.length) {
    return {
      unit: baseUnit,
      scale: 1,
      normalizedData: datas || [],
    };
  }

  const allValues: number[] = datas
    .flatMap(item => item[seriesKey] ?? [])
    .map(s => Number(s?.[valueKey]))
    .filter(v => Number.isFinite(v) && v !== 0);

  const needConvert = requireAll
    ? allValues.length > 0 && allValues.every(v => Math.abs(v) >= threshold)
    : allValues.length > 0 && Math.max(...allValues) >= threshold;

  const scale = needConvert ? threshold : 1;
  const unit = needConvert ? `${prefix}${baseUnit}` : baseUnit;

  const normalizedData = datas.map(item => {
    const series = item[seriesKey] ?? [];

    return {
      ...item,
      [seriesKey]: series.map((s: any) => {
        const raw = Number(s?.[valueKey]);
        if (!Number.isFinite(raw)) return s;

        return {
          ...s,
          [valueKey]: Number((raw / scale).toFixed(fixed)),
        };
      }),
    };
  });

  return {
    unit,
    scale,
    normalizedData,
  };
}

export function normalizeFieldSeriesUnit<T extends Record<string, any>>(
  datas: T[] | undefined,
  options: {
    valueKeys: string[];
    baseUnit: string;
    threshold?: number;
    fixed?: number;
    prefix?: string;
    requireAll?: boolean;
  }
) {
  const { valueKeys, baseUnit, threshold = 10000, fixed = 1, prefix = '万', requireAll = false } = options;

  if (!datas?.length) {
    return { unit: baseUnit, scale: 1, normalizedData: datas || [] };
  }

  // 1️⃣ 单值维度
  const allValues: number[] = datas
    .flatMap(item => valueKeys.map(k => Number(item[k])))
    .filter(v => Number.isFinite(v) && v !== 0);

  // 2️⃣ 堆叠总和维度（关键新增）
  const sumByItem = datas.map(item =>
    valueKeys.reduce((sum, k) => {
      const v = Number(item[k]);
      return Number.isFinite(v) ? sum + v : sum;
    }, 0)
  );

  const hasSumOverThreshold = sumByItem.some(sum => Math.abs(sum) >= threshold);

  // 3️⃣ 最终判断
  const needConvert =
    hasSumOverThreshold ||
    (requireAll
      ? allValues.length > 0 && allValues.every(v => Math.abs(v) >= threshold)
      : allValues.length > 0 && Math.max(...allValues) >= threshold);

  const scale = needConvert ? threshold : 1;
  const unit = needConvert ? `${prefix}${baseUnit}` : baseUnit;

  const normalizedData = datas.map(item => {
    const next = { ...item } as Record<string, any>;

    valueKeys.forEach(k => {
      const raw = Number(item[k]);
      if (Number.isFinite(raw)) {
        next[k] = Number((raw / scale).toFixed(fixed));
      }
    });

    return next as T;
  });

  return { unit, scale, normalizedData };
}

export function normalizeStackedListUnit<T extends Record<string, any>>(
  datas: T[] | undefined,
  options: {
    listKey: string; // usageDataList
    valueKey: string; // value
    baseUnit: string; // kWh
    threshold?: number; // 10000
    fixed?: number; // 1
    prefix?: string; // 万
  }
) {
  const { listKey, valueKey, baseUnit, threshold = 10000, fixed = 1, prefix = '万' } = options;

  if (!datas?.length) {
    return { unit: baseUnit, scale: 1, normalizedData: datas || [] };
  }

  /** 1️⃣ 计算每个 time 的堆叠和 */
  const sumByItem = datas.map(item => {
    const list = Array.isArray(item[listKey]) ? item[listKey] : [];
    return list.reduce((sum: number, row: any) => {
      const v = Number(row?.[valueKey]);
      return Number.isFinite(v) ? sum + v : sum;
    }, 0);
  });

  /** 2️⃣ 是否需要换算（只要有一天超过阈值） */
  const needConvert = sumByItem.some(sum => Math.abs(sum) >= threshold);

  const scale = needConvert ? threshold : 1;
  const unit = needConvert ? `${prefix}${baseUnit}` : baseUnit;

  /** 3️⃣ 回写数据 */
  const normalizedData = datas.map(item => {
    const list = Array.isArray(item[listKey]) ? item[listKey] : null;
    if (!list) return item;

    const newList = list.map(row => {
      const raw = Number(row?.[valueKey]);
      if (!Number.isFinite(raw)) return row;

      return {
        ...row,
        [valueKey]: Number((raw / scale).toFixed(fixed)),
      };
    });

    return {
      ...item,
      [listKey]: newList,
    };
  });

  return {
    unit,
    scale,
    normalizedData,
  };
}

type NumberLikeKeys<T> = {
  [K in keyof T]-?: T[K] extends number | null | undefined ? K : never;
}[keyof T];

type WithSquareList<Item = any> = {
  squareList?: Item[];
};

type NormalizeOptions<K extends PropertyKey> = {
  valueKey: K; // 要转换的字段
  threshold?: number; // 默认 10000
  baseUnit: string; // 单位，如 kWh、m³
  fixedDigits?: number; // 保留的小数位数，默认 1
};

export function normalizeSquareListUnit<
  Medium extends WithSquareList<Item>,
  Item extends Record<string, any>,
  K extends NumberLikeKeys<Item>
>(data: Medium[] | undefined, options: NormalizeOptions<K>) {
  const { valueKey, threshold = 10000, baseUnit, fixedDigits = 1 } = options;

  if (!data || data.length === 0) {
    return {
      normalizedData: data,
      unit: baseUnit,
    };
  }

  /** 1️⃣ 收集所有 value */
  const allValues: number[] = [];

  data.forEach(medium => {
    medium.squareList?.forEach(item => {
      const v = item[valueKey];
      if (typeof v === 'number') {
        allValues.push(Math.abs(v));
      }
    });
  });

  /** 2️⃣ 是否需要转换 */
  const needConvert = allValues.length > 0 && Math.max(...allValues) >= threshold;

  const divisor = needConvert ? threshold : 1;
  const unit = needConvert ? `万${baseUnit}` : baseUnit;

  /** 3️⃣ 转换 + 保留 fixedDigits 位小数 */
  const normalizedData = data.map(medium => ({
    ...medium,
    squareList: medium.squareList?.map(item => {
      const v = item[valueKey];
      if (typeof v === 'number') {
        const converted = v / divisor;
        // 保留 fixedDigits 位小数
        const factor = Math.pow(10, fixedDigits);
        return {
          ...item,
          [valueKey]: Math.round(converted * factor) / factor,
        };
      }
      return item;
    }),
  }));

  return {
    normalizedData,
    unit,
  };
}
